/**
 * Phone-to-congregation WebRTC, phase 1.
 *
 * One phone publishes; viewers on the public church page receive. The phone
 * holds a separate RTCPeerConnection per viewer and uploads a copy of the
 * video to each, so the ceiling is the phone's uplink, not the server. The
 * backend caps viewers for that reason.
 *
 * Both hooks fetch ICE configuration at runtime from the backend, so a TURN
 * relay can be added or rotated without rebuilding the frontend.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { describeCameraError, requestCameraStream } from '@/lib/cameraAccess';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const wsBase = () => API_URL.replace(/^http/, 'ws');

/** ICE configuration, asked for once per hook instance. */
async function iceConfig() {
  try {
    const res = await fetch(`${API_URL}/api/church/live/ice-servers`);
    if (!res.ok) throw new Error('ice');
    const body = await res.json();
    return {
      iceServers: body.ice_servers || [],
      hasRelay: !!body.has_relay,
      maxViewers: body.max_viewers || 6,
    };
  } catch {
    // Still try: STUN-only works on friendly networks, and failing to fetch
    // config should not be the thing that stops a service going out.
    return {
      iceServers: [{ urls: ['stun:stun.l.google.com:19302'] }],
      hasRelay: false,
      maxViewers: 6,
    };
  }
}

/**
 * The phone. Opens the camera, then answers each viewer that arrives with its
 * own offer.
 */
export function useChurchPublisher(churchId) {
  const [status, setStatus] = useState('idle');   // idle|starting|live|error
  const [error, setError] = useState('');
  const [errorSteps, setErrorSteps] = useState([]);
  const [hasAudio, setHasAudio] = useState(true);
  const [viewers, setViewers] = useState(0);
  const [hasRelay, setHasRelay] = useState(true);
  const [facing, setFacing] = useState('environment');

  const streamRef = useRef(null);
  const wsRef = useRef(null);
  const peersRef = useRef(new Map());
  const configRef = useRef(null);
  const videoRef = useRef(null);

  const attach = useCallback((el) => {
    videoRef.current = el;
    if (el && streamRef.current) el.srcObject = streamRef.current;
  }, []);

  // Asked for up front, not on the way live: an admin should know their
  // congregation may not be able to connect before they start the service,
  // not after.
  useEffect(() => {
    let dead = false;
    iceConfig().then((cfg) => {
      if (!dead) { configRef.current = cfg; setHasRelay(cfg.hasRelay); }
    });
    return () => { dead = true; };
  }, []);

  /** The camera, on its own, so the preview appears before going live. */
  const openCamera = useCallback(async (which = facing) => {
    setError('');
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      const stream = await (async () => {
        const got = await requestCameraStream(which);
        setHasAudio(got.hasAudio);
        return got.stream;
      })();
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setFacing(which);
      // Swapping camera mid-broadcast: replace the track on every open
      // connection rather than renegotiating with each viewer.
      const next = stream.getVideoTracks()[0];
      peersRef.current.forEach(({ pc }) => {
        const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'video');
        if (sender && next) sender.replaceTrack(next);
      });
      return true;
    } catch (e) {
      // Never leave the sentence hanging, and never stop at "allow access":
      // on a phone the permission is usually needed from the operating system
      // as well as from the browser, so say which and where.
      const { message, steps } = describeCameraError(e);
      setError(message);
      setErrorSteps(steps);
      setStatus('error');
      return false;
    }
  }, [facing]);

  const offerTo = useCallback(async (peerId) => {
    const cfg = configRef.current;
    const pc = new RTCPeerConnection({ iceServers: cfg.iceServers });
    peersRef.current.set(peerId, { pc });
    setViewers(peersRef.current.size);

    streamRef.current.getTracks().forEach((t) => pc.addTrack(t, streamRef.current));
    pc.onicecandidate = (e) => {
      if (e.candidate && wsRef.current?.readyState === 1) {
        wsRef.current.send(JSON.stringify({
          type: 'ice', target: peerId, candidate: e.candidate,
        }));
      }
    };
    pc.onconnectionstatechange = () => {
      if (['failed', 'closed'].includes(pc.connectionState)) {
        pc.close();
        peersRef.current.delete(peerId);
        setViewers(peersRef.current.size);
      }
    };
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    wsRef.current.send(JSON.stringify({ type: 'offer', target: peerId, sdp: offer }));
  }, []);

  const goLive = useCallback(async () => {
    setStatus('starting');
    const cfg = configRef.current || await iceConfig();
    configRef.current = cfg;
    setHasRelay(cfg.hasRelay);

    if (!streamRef.current && !(await openCamera())) return false;

    const token = localStorage.getItem('admin_token')
      || localStorage.getItem('eventflow_token') || localStorage.getItem('token');
    const ws = new WebSocket(
      `${wsBase()}/api/ws/church-live/${churchId}?role=publisher&token=${encodeURIComponent(token || '')}`);
    wsRef.current = ws;

    return new Promise((resolve) => {
      let settled = false;
      ws.onmessage = async (ev) => {
        const msg = JSON.parse(ev.data);
        if (msg.type === 'ready') {
          setStatus('live');
          (msg.viewers || []).forEach((v) => offerTo(v));
          if (!settled) { settled = true; resolve(true); }
        } else if (msg.type === 'viewer_joined') {
          await offerTo(msg.peer_id);
        } else if (msg.type === 'viewer_left') {
          const entry = peersRef.current.get(msg.peer_id);
          if (entry) { entry.pc.close(); peersRef.current.delete(msg.peer_id); }
          setViewers(peersRef.current.size);
        } else if (msg.type === 'answer') {
          const entry = peersRef.current.get(msg.peer_id);
          if (entry) await entry.pc.setRemoteDescription(msg.sdp);
        } else if (msg.type === 'ice') {
          const entry = peersRef.current.get(msg.peer_id);
          if (entry && msg.candidate) {
            try { await entry.pc.addIceCandidate(msg.candidate); } catch { /* late candidate */ }
          }
        } else if (msg.type === 'error') {
          setError(msg.message);
          setStatus('error');
          if (!settled) { settled = true; resolve(false); }
        }
      };
      ws.onclose = (e) => {
        if (!settled) {
          settled = true;
          setError(e.code === 4401
            ? 'You are not signed in as someone who can broadcast for this church.'
            : 'The connection to the server closed.');
          setStatus('error');
          resolve(false);
        } else if (status === 'live') {
          setStatus('idle');
        }
      };
      ws.onerror = () => {
        if (!settled) {
          settled = true;
          setError('Could not reach the streaming server.');
          setStatus('error');
          resolve(false);
        }
      };
    });
  }, [churchId, openCamera, offerTo, status]);

  const stop = useCallback(() => {
    try { wsRef.current?.send(JSON.stringify({ type: 'stop' })); } catch { /* already gone */ }
    wsRef.current?.close();
    wsRef.current = null;
    peersRef.current.forEach(({ pc }) => pc.close());
    peersRef.current.clear();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setViewers(0);
    setStatus('idle');
  }, []);

  useEffect(() => () => {
    // Leaving the page must take the church off air, not leave a phone
    // broadcasting silently in a background tab.
    try { wsRef.current?.close(); } catch { /* ignore */ }
    peersRef.current.forEach(({ pc }) => pc.close());
    streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  return { status, error, errorSteps, hasAudio, viewers, hasRelay, facing,
           attach, openCamera, goLive, stop };
}

/** A viewer on the public church page. Receive-only. */
export function useChurchViewer(churchId, enabled) {
  // connecting|playing|waiting|stalled|failed|offline
  const [state, setState] = useState('connecting');
  const [message, setMessage] = useState('');
  const videoRef = useRef(null);
  const pcRef = useRef(null);
  const wsRef = useRef(null);

  const attach = useCallback((el) => {
    videoRef.current = el;
  }, []);

  useEffect(() => {
    if (!enabled || !churchId) return undefined;
    let dead = false;
    const peerId = `v-${Math.random().toString(36).slice(2, 10)}`;

    (async () => {
      const cfg = await iceConfig();
      if (dead) return;
      const ws = new WebSocket(
        `${wsBase()}/api/ws/church-live/${churchId}?role=viewer&peer_id=${peerId}`);
      wsRef.current = ws;

      ws.onmessage = async (ev) => {
        const msg = JSON.parse(ev.data);
        if (msg.type === 'ready') {
          setState(msg.live ? 'connecting' : 'waiting');
          if (!msg.live) setMessage('Waiting for the broadcast to start…');
        } else if (msg.type === 'live') {
          setState('connecting');
        } else if (msg.type === 'offer') {
          const pc = new RTCPeerConnection({ iceServers: cfg.iceServers });
          pcRef.current = pc;
          pc.ontrack = (e) => {
            if (videoRef.current) {
              videoRef.current.srcObject = e.streams[0];
              setState('playing');
            }
          };
          pc.onicecandidate = (e) => {
            if (e.candidate && ws.readyState === 1) {
              ws.send(JSON.stringify({ type: 'ice', candidate: e.candidate }));
            }
          };
          pc.onconnectionstatechange = () => {
            if (pc.connectionState === 'failed') {
              setState('failed');
              setMessage(cfg.hasRelay
                ? 'Could not reach the broadcast.'
                : 'Your network will not allow a direct connection to the '
                  + 'broadcasting phone, and no relay server is configured.');
            }
          };
          await pc.setRemoteDescription(msg.sdp);
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          ws.send(JSON.stringify({ type: 'answer', sdp: answer }));
        } else if (msg.type === 'ice') {
          if (pcRef.current && msg.candidate) {
            try { await pcRef.current.addIceCandidate(msg.candidate); } catch { /* late */ }
          }
        } else if (msg.type === 'publisher_left') {
          setState('offline');
          setMessage('The broadcast has ended.');
          pcRef.current?.close();
        } else if (msg.type === 'error') {
          setState('failed');
          setMessage(msg.message);
        }
      };
      ws.onerror = () => { if (!dead) setState('failed'); };
    })();

    return () => {
      dead = true;
      try { wsRef.current?.close(); } catch { /* ignore */ }
      pcRef.current?.close();
    };
  }, [churchId, enabled]);

  // "Connecting" that never resolves is the worst of the states: the page says
  // the service is live and shows a spinner for the rest of the hour. A quarter
  // of a minute is long enough for any honest negotiation, so after that say
  // plainly that the picture is not getting through.
  useEffect(() => {
    if (!enabled || state !== 'connecting') return undefined;
    const id = setTimeout(() => {
      setState('stalled');
      setMessage('The service is broadcasting, but the picture is not reaching '
                 + 'this device. Try a different network, or watch on one of '
                 + 'the links below if the church has added any.');
    }, 25_000);
    return () => clearTimeout(id);
  }, [state, enabled]);

  return { state, message, attach };
}
